import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FieldType, SearchOperators } from '@seahorse/domain';
import { ShInputComponent, ShSelectComponent } from '@seahorse/components';

/** One operator in the operator select. */
export interface OperatorOption {
  label: string;
  value: SearchOperators;
}

const OPERATOR_KEYS: Record<SearchOperators, string> = {
  [SearchOperators.Equals]: 'shared.operators.equals',
  [SearchOperators.Contains]: 'shared.operators.contains',
  [SearchOperators.StartsWith]: 'shared.operators.startsWith',
  [SearchOperators.EndsWith]: 'shared.operators.endsWith',
  [SearchOperators.GreaterThan]: 'shared.operators.greaterThan',
  [SearchOperators.LesserThan]: 'shared.operators.lesserThan',
  [SearchOperators.InArray]: 'shared.operators.inArray',
  [SearchOperators.Between]: 'shared.operators.between',
  [SearchOperators.NotEquals]: 'shared.operators.notEquals',
  [SearchOperators.ContainsAny]: 'shared.operators.containsAny',
  [SearchOperators.IsEmpty]: 'shared.operators.isEmpty',
  [SearchOperators.IsNotEmpty]: 'shared.operators.isNotEmpty',
};

/** The operators a filter on a field of this type offers. */
export function operatorsForFieldType(
  fieldType: number | null
): SearchOperators[] {
  switch (fieldType) {
    case FieldType.SingleLineText:
    case FieldType.MultiLineText:
    case FieldType.File:
    case FieldType.AutoNumbering:
      return [
        SearchOperators.Equals,
        SearchOperators.Contains,
        SearchOperators.StartsWith,
        SearchOperators.EndsWith,
        SearchOperators.InArray,
        SearchOperators.NotEquals,
        SearchOperators.IsEmpty,
        SearchOperators.IsNotEmpty,
      ];
    case FieldType.DateTime:
    case FieldType.Date:
    case FieldType.Integer:
    case FieldType.Decimal:
      return [
        SearchOperators.Equals,
        SearchOperators.GreaterThan,
        SearchOperators.LesserThan,
        SearchOperators.Between,
        SearchOperators.NotEquals,
        SearchOperators.IsEmpty,
        SearchOperators.IsNotEmpty,
      ];
    case FieldType.Boolean:
      return [
        SearchOperators.Equals,
        SearchOperators.NotEquals,
        SearchOperators.IsEmpty,
        SearchOperators.IsNotEmpty,
      ];
    case FieldType.List:
      return [
        SearchOperators.Equals,
        SearchOperators.InArray,
        SearchOperators.NotEquals,
        SearchOperators.IsEmpty,
        SearchOperators.IsNotEmpty,
      ];
    default:
      return [SearchOperators.IsEmpty, SearchOperators.IsNotEmpty];
  }
}

/**
 * The operator and the value(s) of one default-filter row: an operator select
 * and, depending on the operator, no value, one value or (between) two. The
 * value is one string, `a` or `a,b` for between, as the filter stores it.
 * Replaces `sh-field-operator-form` of libs/ui (Bootstrap markup).
 */
@Component({
  selector: 'su-field-operator-input',
  templateUrl: './field-operator-input.component.html',
  imports: [CommonModule, TranslateModule, ShSelectComponent, ShInputComponent],
})
export class FieldOperatorInputComponent {
  private readonly _translate = inject(TranslateService);

  SearchOperators = SearchOperators;

  @Input() operator: number | null = null;
  @Output() operatorChange = new EventEmitter<number>();

  private _fieldType: number | null = null;
  @Input() set fieldType(value: number | null) {
    this._fieldType = value;
    // A new array only when the type changes: it feeds a PrimeNG input.
    this.operatorOptions = operatorsForFieldType(value).map((operator) => ({
      label: this._translate.instant(OPERATOR_KEYS[operator]),
      value: operator,
    }));
  }
  get fieldType(): number | null {
    return this._fieldType;
  }

  @Input() set fieldValue(value: unknown) {
    this.readValue(value);
  }
  @Output() fieldValueChange = new EventEmitter<string>();

  operatorOptions: OperatorOption[] = [];
  value1: string | null = null;
  value2: string | null = null;

  get hasValue(): boolean {
    // == in stead of === is on purpose (the operator may be a string)
    return (
      this.operator != SearchOperators.IsEmpty &&
      this.operator != SearchOperators.IsNotEmpty
    );
  }

  get isBetween(): boolean {
    return this.operator == SearchOperators.Between;
  }

  setOperator(operator: unknown) {
    this.operator = operator === null ? null : Number(operator);
    if (this.operator !== null) {
      this.operatorChange.emit(this.operator);
    }
    // The stored value depends on the operator (between has two parts).
    this.emitValue();
  }

  setValue1(value: string | null) {
    this.value1 = value;
    this.emitValue();
  }

  setValue2(value: string | null) {
    this.value2 = value;
    this.emitValue();
  }

  private emitValue() {
    this.fieldValueChange.emit(this.composeValue());
  }

  private composeValue(): string {
    if (this.isBetween) {
      return `${this.value1 ?? ''},${this.value2 ?? ''}`;
    }
    return this.value1 ?? '';
  }

  private readValue(input: unknown) {
    if (input === null || input === undefined) {
      return;
    }

    const text = String(input);
    if (text === this.composeValue()) {
      return;
    }

    if (this.isBetween) {
      const values = text.split(',');
      this.value1 = values[0] ?? null;
      this.value2 = values.length > 1 ? values[1] : null;
    } else {
      this.value1 = text;
      this.value2 = null;
    }
  }
}
